import React, { useState, useEffect, useRef } from 'react';
import styles from './ShabanSearchResults.module.scss';
import {
  Configure,
  Index,
  //InfiniteHits,
  InstantSearch,
  UseInfiniteHitsProps,
  UseSortByProps,
  useInfiniteHits,
  useInstantSearch,
  useSortBy,
  useStats,
} from 'react-instantsearch';
import type { BaseHit, Hit } from 'instantsearch.js';
import { SHABAN, SHABAN_SORT, leumitSearchClient } from 'assets/constants';
import { ShabanResultType } from 'types';
import { checkIfParamExist, getAllClassNames } from 'helpers';
import strings from "assets/strings";
import Text from "components/text/Text";
import getRouting from './routing';
import { ShabanRefinements } from './ShabanRefinements/ShabanRefinements';
import { ShabanResult } from './ShabanResult';
import Popup from 'components/popup/Popup';
import { FilterButton } from 'components/buttons/filterButton/FilterButton';
import { CustomSortDropdown } from 'components/customSortDropdown/CustomSortDropdown';
import SvgGenerator from 'components/svgGenerator/SvgGenerator';
import { VirtualQuery } from 'components/virtualQuery/VirtualQuery';
import { localAttributes } from 'assets/constants';
import { CustomalertPopup } from "components/popup/customAlertPopup/CustomalertPopup";


function NoResultsBoundary({ children, fallback }: { children: React.ReactNode; fallback: React.ReactNode }) {
  const { results } = useInstantSearch();

  const extendedResults = results as typeof results & {
    __isError?: boolean;
    __errorStatus?: number;
  };

  if (extendedResults.__isError && extendedResults.__errorStatus === 403) {
    return (
      <div className="noMessagesWrapper">
        {/* <SvgGenerator
          name="Magnifier"
          className="magnifierIcon"
          objectName="medicalCenterSearchResults"
        /> */}
        <Text text={strings.globalStrings.networkError} />
      </div>
    );
  }

  if (!results.__isArtificial && results.nbHits === 0) {
    return (
      <>
        {fallback}
        <div hidden>{children}</div>
      </>
    );
  }

  return <>{children}</>;
}

function NoResults() {
  const noResultsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (noResultsRef.current) {
      noResultsRef.current.focus();
    }
  }, []);
  return (
    <div
      className="noMessagesWrapper"
      role="status"
      aria-live="assertive"
      tabIndex={-1}
      ref={noResultsRef}
    >
      <SvgGenerator name="Magnifier" className="magnifierIcon" objectName="doctorSearchResults" />
      <Text text={strings.globalStrings.noResultsFound} />
    </div>
  );
}

export function ShabanSearchResults() {
  const routing = getRouting(SHABAN);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 992);
  const [operationName, setOperationName] = useState('');
  const [operationCode, setOperationCode] = useState('');
  const [code, setCode] = useState('');
  const popupRef = useRef<HTMLDivElement>(null);
  const tooltipPopupRef = useRef<HTMLDivElement>(null);

  const RenderResults = () => {
    const [hitsResults, setHitsResults] = useState<{ indexName: string, total: number }[]>([
      { indexName: SHABAN, total: 0 },
      { indexName: 'exact', total: 0 },
      { indexName: 'geo', total: 0 }
    ]);
    const [medicalHits, setMedicalCenterHits] = useState(0);
    const [exactHits, setExactHits] = useState(0);
    const [geoHits, setGeoHits] = useState(0);
    const { scopedResults } = useInstantSearch();
    //const [isFinalResults, setIsFinalResults] = useState(false);

    useEffect(() => {     
      // This function will be called whenever scopedResults changes.
      function calculateExactStats() {
        let hitsIndexes = [
          { indexName: SHABAN, total: 0 },
          { indexName: 'exact', total: 0 },
          { indexName: 'geo', total: 0 }
        ];

        let medicalCenterIndex = scopedResults.filter(i => i.indexId === SHABAN);
        let exactindex = scopedResults.filter(i => i.indexId === 'exact');
        let geoindex = scopedResults.filter(i => i.indexId === 'geo');

        if (medicalCenterIndex.length) {
          hitsIndexes[0].total != medicalCenterIndex[0].results?.nbHits ? setMedicalCenterHits(medicalCenterIndex[0].results?.nbHits || 0) : null;
          hitsIndexes[0].total = medicalCenterIndex[0].results?.nbHits || 0;
        }
        if (exactindex.length) {
          hitsIndexes[1].total != exactindex[0].results?.nbHits ? setExactHits(exactindex[0].results?.nbHits || 0) : null;
          hitsIndexes[1].total = exactindex[0].results?.nbHits || 0;
        }
        if (geoindex.length) {
          hitsIndexes[1].total != exactindex[0].results?.nbHits ? setGeoHits(exactindex[0].results?.nbHits || 0) : null;
          hitsIndexes[1].total = exactindex[0].results?.nbHits || 0;
        }
       
        return hitsIndexes;
      }

      // Calculate the exact stats and update the state.
      const newHitsResults = calculateExactStats();
      setHitsResults(newHitsResults);
      if (scopedResults[0].results?.hits[0]) {
        setOperationName(scopedResults[0].results?.hits[0].operationName);
        setOperationCode(scopedResults[0].results?.hits[0].operationCode);
        setCode(`ICD9 ${scopedResults[0].results?.hits[0].icd9}`);
      }

      // if (scopedResults.length > 0) {
      //   // Check if all indices have results
      //   const allResultsFetched = scopedResults.every(result => result.results?.hits && !result.results?.exhaustiveNbHits);
      //   setIsFinalResults(allResultsFetched);
      // }

    }, [scopedResults, medicalHits, exactHits, geoHits]); // Only re-run the effect if scopedResults changes


    localStorage.setItem('hitsResults', JSON.stringify(hitsResults));
    if (!checkIfParamExist('loc')) {
      return <InfiniteHitsComponent />
    }
    else if (exactHits < 5) {
      return <><Index indexName={SHABAN} indexId="exact">
        <Configure />
        <GeoInfiniteHitsComponent />
        {geoHits > 0 && <div className="resultsAround">
          <SvgGenerator name="Exclamation" className="exclamationIcon" objectName="medicalCenterSearchResults" />
          {/* <Exclamation className="exclamationIcon" /> */}
          <Text text={strings.globalStrings.foundResultsAroundLocation} />
        </div>
        }
      </Index>
        <Index indexName={SHABAN} indexId="geo">
          <Configure />
          <InfiniteHitsComponent />
        </Index>
      </>
    }
    else {
      return <Index indexName={SHABAN} indexId="exact">
        <Configure />
        <InfiniteHitsComponent />
      </Index>
    }
  }

  function GeoInfiniteHitsComponent(props: UseInfiniteHitsProps) {
    const { hits } = useInfiniteHits(props);
    return (
      <div>
        {hits.map((hit: Hit<BaseHit>) => {
          return <ShabanResult hit={hit as ShabanResultType} />;
        })
        }
      </div>
    )

  }

  // function InfiniteHitsComponent() {
  //   return (
  //     <InfiniteHits
  //       classNames={{
  //         item: "ais-InfiniteHits-item",
  //         loadPrevious: "ais-PreviousResults",
  //         loadMore: "ais-InfiniteHits-loadMore",
  //       }}
  //       translations={{
  //         showMoreButtonText: strings.globalStrings.moreResults,
  //       }}
  //       hitComponent={({ hit }: { hit: Hit<BaseHit> }) => {
  //         return <ShabanResult hit={hit as ShabanResultType} />;
  //       }}
  //     />
  //   )
  // }

  function InfiniteHitsComponent(props: UseInfiniteHitsProps) {
    const { hits, showMore, isLastPage } = useInfiniteHits(props);
    const [nextFocusIndex, setCurrentFocusIndex] = React.useState(0);
    const doctorResultRef = useRef<HTMLDivElement>(null);

    const handleShowMore = () => {
      showMore();
      setCurrentFocusIndex(nextFocusIndex + 20);
      doctorResultRef.current?.focus();
    }

    return (
      <div className='animate'>
        {hits.map((hit: Hit<BaseHit>) => {
          return <ShabanResult hit={hit as ShabanResultType} />;
        })
        }
        {!isLastPage && <button className='ais-InfiniteHits-loadMore' onClick={handleShowMore} disabled={isLastPage}>{strings.globalStrings.moreResults}</button>}
      </div>
    )

  }


  const TopRefinement: React.FC = () => {
    const { nbHits } = useStats();
    //const storedHitsResults = localStorage.getItem('hitsResults') || '';
    // use nbHits to check if there are any results 
    //const resultsTotal = storedHitsResults ? JSON.parse(storedHitsResults).map((res: any) => res.total > 0) : -1;

    return (
      //use nbHits to check if there are any results 
      <div className={styles.container}>
        <div className={styles.divResults}>
          <Stats />
          {checkIfParamExist('loc') && !isMobile ? <>
            <Text
              className={styles.text}
              text={`${strings.globalStrings.sortBy}`} />
            <CustomSortBy
              items={[
                { label: "שם", value: SHABAN },
                { label: "מרחק", value: SHABAN_SORT },
              ]} />
          </> : null}
        </div>
        {nbHits > 0 && <OperationDetails />}
      </div>
    );
  };
  const Stats = () => {
    const { nbHits } = useStats();
    if (nbHits === 0) return null;
    return (
      nbHits === 1 ?
        <Text
          className={styles.title}
          text={strings.globalStrings.foundOneResult} /> :
        <Text
          className={styles.title}
          text={`${strings.medicalCentersSearchResults.found} ${nbHits} ${strings.medicalCentersSearchResults.results}`}
        />
    );
  };

  const OperationDetails = () => {
    return (
      <div className={styles.operationDetailsWrapper}>
        <div>
          <>
            <Text text={`${strings.operationSearchResults.resultsForOperation} `} style={{ marginInlineEnd: '.5ch', fontWeight: '500' }} />
            <Text text={`${operationName}`} className={styles.operationName} />
          </>
        </div>
        <div style={{ display: 'flex', gap: '16px', fontSize: '16px' }}>
          <div className={styles.operation}>
            <Text text={`${strings.operationSearchResults.operationCode} `} style={{ fontSize: '16px', color: '#2e567a', fontWeight: '500' }} /><Text className={styles.operationCode} text={operationCode} />
          </div>
          <div className={styles.operation}>
  <Text text={`${strings.operationSearchResults.code}`} style={{ fontSize: '16px', color: '#2e567a', fontWeight: '500' }} /><Text className={styles.operationCode} text={code} />
</div>
{strings.operationSearchResults.tooltipTitle && (
  <>
    <span
      className={styles.tooltipTrigger}
      onClick={handleTooltipToggle}
      role="button"
      tabIndex={0}
    >
      <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 17 17"><path id="question-circle-fill" d="M16,8A8,8,0,1,1,8,0a8,8,0,0,1,8,8ZM5.5,6.033h.825a.278.278,0,0,0,.266-.25A1.249,1.249,0,0,1,7.929,4.649,1.162,1.162,0,0,1,9.243,5.817c0,.635-.374.927-.965,1.371A2.277,2.277,0,0,0,7.11,9.175l0,.217a.25.25,0,0,0,.25.246h.811a.25.25,0,0,0,.25-.25V9.283c0-.718.273-.927,1.01-1.486a2.44,2.44,0,0,0,1.244-2.056C10.678,4.23,9.4,3.5,8,3.5a2.459,2.459,0,0,0-2.75,2.286.237.237,0,0,0,.241.247Zm2.325,6.443a.938.938,0,1,0,0-1.867.936.936,0,1,0,0,1.867Z" transform="translate(0.5 0.5)" fill="#9a3f82" stroke="rgba(0,0,0,0)" stroke-width="1"></path></svg>
      {strings.operationSearchResults.tooltipTitle}
    </span>
    <Popup
      ref={tooltipPopupRef}
      style={{ padding: '25px', height: 'auto' }}
      height={'auto'}
      width={690}
      onClose={handleTooltipToggle}
    >
      <CustomalertPopup
        title={strings.operationSearchResults.tooltipTitle}
        titeStyle={{ padding: '7px 0 17px' }}
        buttonText=""
        onClose={handleTooltipToggle}
        showButton={false}
        paragraphs={[
          {
            text: strings.operationSearchResults.tooltipContent,
            style: { whiteSpace: 'pre-line', lineHeight: 1.5 },
            isHtml: true
          }
        ]}
      />
    </Popup>
  </>
)}
        </div>
      </div>
    );
  }


  const CustomSortBy = (props: UseSortByProps) => {
    const { options, currentRefinement = options[0].value, refine } = useSortBy(
      props
    );
    return (
      <div className={styles.sortContainer}>
        {options.map((option, index) => (
          <React.Fragment key={option.value}>
            <span
              className={getAllClassNames([
                styles.spanLabel,
                option.value === currentRefinement ? styles.active : undefined,
              ])}
              key={option.value}
              onClick={() => refine(option.value)}
            >
              {option.label}
            </span>
            {index === options.length - 1 ? null : (
              <span className={styles.divider} />
            )}
          </React.Fragment>
        ))}
      </div>
    );
  };

  const handleResize = () => {
    setIsMobile(window.innerWidth < 992);
  };

  useEffect(() => {
    // Set up the event listener
    window.addEventListener('resize', handleResize);

    // Clean up the event listener on component unmount
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const handleToggleVisibility = () => {
    if (popupRef.current) {
      popupRef.current.classList.toggle('show');
    }
  };

  const handleTooltipToggle = () => {
    if (tooltipPopupRef.current) {
      tooltipPopupRef.current.classList.toggle('show');
    }
  };

  return (
    <div className="row two-col-layout ais-InstantSearch">
      <InstantSearch
        indexName={SHABAN}
        searchClient={leumitSearchClient}
        routing={routing}
      >
        <Configure snippetEllipsisText="…" hitsPerPage={20} />
        {isMobile &&
          <Popup
            ref={popupRef}
            onClose={() => handleToggleVisibility()}
          >
            <ShabanRefinements />
          </Popup>

        }
        {!isMobile && <ShabanRefinements />
        }

        <div className="col-12 col-xl-9 col-lg-8 ais-SearchResults">
          {isMobile &&
            <div className={getAllClassNames([styles.divMobileTop])}>
              <FilterButton onClick={() => handleToggleVisibility()} text={strings.globalStrings.filterBy} />
              {checkIfParamExist('loc') && <CustomSortDropdown items={[
                { label: "שם", value: SHABAN, items: [] },
                { label: "מרחק", value: SHABAN_SORT, items: [] },
              ]} />
              }
            </div>
          }
          <TopRefinement />
          <VirtualQuery query={localAttributes.localDoctorFieldName} />
          <NoResultsBoundary fallback={<NoResults />}>
            <RenderResults />
          </NoResultsBoundary>
        </div>
      </InstantSearch>
    </div>
  );
}
